import { Correct } from '../../components/Correct'
import { LineIcon } from '../../components/LineIcon'
import type { AccountHealth as Health, HealthGauge } from '../../types/accounts'

// The account health dashboard: five gauges the API computed on read (coverage, reach, momentum, pipeline,
// risk), each with its number, its one-line why, and a press that opens the panel holding its evidence; the
// overall word with the reasons behind it. Presentation only — the math and the weights live in the API.

const WORD_TONE: Record<Health['word'], string> = { strong: 'ok', steady: 'brand', 'at risk': 'warn', cold: 'danger' }

export function AccountHealth({ health, onEvidence, companyId, companyName }: {
  health: Health; onEvidence: (evidence: HealthGauge['evidence']) => void; companyId: string; companyName: string
}) {
  const a = health.activity
  return (
    <section className="card health-card" aria-label="Account health">
      <div className="card-head">
        <div>
          <p className="kicker">Account health</p>
          <div className="health-word-row">
            <span className={`health-word ${WORD_TONE[health.word]}`}>{health.word}</span>
            <span className="muted small">read from the plan, as of today</span>
            <Correct kind="account-health" entityId={companyId} entityLabel={companyName} currentValue={health.word.replace(' ', '-')} compact />
          </div>
        </div>
      </div>
      <div className="gauges">
        {health.gauges.map((g) => <Gauge key={g.key} gauge={g} onClick={() => onEvidence(g.evidence)} />)}
      </div>
      <ul className="health-reasons">
        {health.reasons.map((r, i) => <li key={i}>{r}</li>)}
      </ul>
      <p className="health-activity muted small">
        Last 30 days: {a.threadsRecent} thread{a.threadsRecent === 1 ? '' : 's'}, {a.meetingsRecent} meeting{a.meetingsRecent === 1 ? '' : 's'}, {a.signalsRecent} signal{a.signalsRecent === 1 ? '' : 's'} · the 30 before: {a.threadsPrior}, {a.meetingsPrior}, {a.signalsPrior}.
      </p>
    </section>
  )
}

function Gauge({ gauge, onClick }: { gauge: HealthGauge; onClick: () => void }) {
  const risk = gauge.key === 'risk'
  // risk reads the other way: a high number is bad
  const tone = risk ? (gauge.score >= 50 ? 'danger' : gauge.score >= 25 ? 'warn' : 'ok') : gauge.score >= 65 ? 'ok' : gauge.score >= 35 ? 'brand' : gauge.score > 0 ? 'warn' : 'danger'
  const r = 26, c = Math.PI * r   // a half-circle arc
  const fraction = Math.max(0, Math.min(1, gauge.score / 100))
  return (
    <button className={`gauge ${tone}`} onClick={onClick} title={`${gauge.label}: ${gauge.why} Opens the evidence.`}>
      <svg viewBox="0 0 64 40" className="gauge-arc" aria-hidden="true">
        <path className="gauge-track" d={`M 6 36 A ${r} ${r} 0 0 1 58 36`} />
        <path className="gauge-fill" d={`M 6 36 A ${r} ${r} 0 0 1 58 36`} strokeDasharray={c} strokeDashoffset={c * (1 - fraction)} />
      </svg>
      <span className="gauge-number">{gauge.score}</span>
      <span className="gauge-label"><LineIcon name={gauge.key} size={13} /> {gauge.label}</span>
      <span className="gauge-why">{gauge.why}</span>
    </button>
  )
}
